import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { Translator } from '@/types/translator.type';

/**
 * Canonical list of hotel pension types used for "other pension" pricing
 * (supplements above the room's base pension). Order matters for getPensionTypesAbove.
 */
export const HOTEL_PENSION_TYPES = [
  'SingleRoom',
  'Breakfast',
  'SemiPension',
  'FullPension',
  'AllInclusive',
] as const satisfies HotelPensionType[];

export const ROOM_PENSION_OPTION_VALUES = [
  ...HOTEL_PENSION_TYPES,
] as const satisfies HotelPensionType[];

export const SINGLE_ROOM_TRANSLATION_KEY =
  'hotels.roomPensionTypes.singleRoom' as const;

/**
 * i18n translation keys for each hotel pension type.
 * Use with t() to get translated labels, or use getHotelPensionTypeLabels(t).
 */
export const HOTEL_PENSION_TYPE_TRANSLATION_KEYS = {
  SingleRoom: 'hotels.roomPensionTypes.singleRoom',
  Breakfast: 'hotels.roomPensionTypes.bedAndBreakfast',
  SemiPension: 'hotels.roomPensionTypes.halfBoard',
  FullPension: 'hotels.roomPensionTypes.fullBoard',
  AllInclusive: 'hotels.roomPensionTypes.allInclusive',
} as const satisfies Record<HotelPensionType, string>;

/**
 * Returns translated labels for all hotel pension types.
 * Use in components/hooks that need Record<HotelPensionType, string>.
 */
export const getHotelPensionTypeLabels = (t: Translator) =>
  ({
    SingleRoom: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS.SingleRoom),
    Breakfast: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS.Breakfast),
    SemiPension: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS.SemiPension),
    FullPension: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS.FullPension),
    AllInclusive: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS.AllInclusive),
  }) as const satisfies Record<HotelPensionType, string>;

export const getRoomPensionOptions = (
  t: Translator,
): { value: HotelPensionType; label: string }[] =>
  HOTEL_PENSION_TYPES.map((type) => ({
    value: type,
    label: t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS[type]),
  }));

const isHotelPensionType = (value: string): value is HotelPensionType => {
  return HOTEL_PENSION_TYPES.includes(value as HotelPensionType);
};

/**
 * Resolves a stored value to HotelPensionType. Accepts the stable enum
 * (SingleRoom, Breakfast, etc.) or a legacy translated label. Use when loading
 * form defaults or API data so the selector always has a stable value.
 */
export const toHotelPensionType = (
  value?: string,
): HotelPensionType | undefined => {
  if (!value) return undefined;
  if (isHotelPensionType(value)) return value;
  return undefined;
};
